import { z } from 'zod'

/**
 * Working calendar (ASM-141): pure working-day arithmetic shared by the API (authoritative) and the SPA
 * (preview), so both compute the same due date.
 *
 * Time model:
 * - Business dates are `YYYY-MM-DD` in Asia/Muscat. Oman keeps UTC+04:00 all year (no daylight saving), so
 *   Muscat wall time is UTC plus a fixed four hours.
 * - A calendar is a week pattern (7 days, Sunday first, each with working hours `HH:MM`–`HH:MM`) plus a set
 *   of holiday dates. A holiday is a non-working day whatever the week pattern says.
 *
 * Counting rules (also ASM-141):
 * - `addWorkingDays`: the start day is excluded and the wall-clock time is kept (Wednesday 14:00 + 2 working
 *   days = Sunday 14:00 on a Sunday–Thursday week).
 * - A start on a non-working day begins at the next working day's start time (Friday 10:00 + 2 = Tuesday
 *   07:30 on the default week: the clock starts Sunday 07:30, which is the excluded start day).
 * - `workingDaysBetween(from, to)` counts the working days in (from, to], so it inverts `addWorkingDays`.
 * - `addWorkingHours` consumes only the working-hours windows; a start outside a window begins at the next
 *   window's start.
 * - Every scan stops after `MAX_SCAN_DAYS` days with a RangeError (a guard against a calendar made of holidays).
 */

/** Fixed offset of Asia/Muscat from UTC (no daylight saving). */
const MUSCAT_OFFSET_MS = 4 * 60 * 60 * 1000
const DAY_MS = 24 * 60 * 60 * 1000
const MINUTE_MS = 60 * 1000

/** A scan for working days or hours gives up after this many days. */
export const MAX_SCAN_DAYS = 3660

const timeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM (00:00–23:59)')

export const workingDaySchema = z
  .object({
    working: z.boolean(),
    start: timeOfDaySchema,
    end: timeOfDaySchema,
  })
  .strict()
  .refine((d) => !d.working || d.start < d.end, {
    message: 'A working day must start before it ends',
    path: ['end'],
  })
export type WorkingDay = z.infer<typeof workingDaySchema>

/** Seven days, Sunday first (index 0 = Sunday … 6 = Saturday), at least one of them working. */
export const weekPatternSchema = z
  .array(workingDaySchema)
  .length(7)
  .refine((days) => days.some((d) => d.working), { message: 'At least one day of the week must be working' })
export type WeekPattern = z.infer<typeof weekPatternSchema>

const workDay: WorkingDay = { working: true, start: '07:30', end: '14:30' }
const offDay: WorkingDay = { working: false, start: '07:30', end: '14:30' }

/** Sunday–Thursday working, 07:30–14:30; Friday and Saturday off (ASM-141 default, tenant data). */
export const DEFAULT_WEEK_PATTERN: WeekPattern = [workDay, workDay, workDay, workDay, workDay, offDay, offDay]

/** What the functions need: the week and a holiday lookup (a `Set<string>` of `YYYY-MM-DD` fits). */
export interface WorkingCalendar {
  week: WeekPattern
  holidays: { has(date: string): boolean }
}

export const TIMER_UNITS = ['HOURS', 'WORKING_HOURS', 'WORKING_DAYS'] as const
export type TimerUnit = (typeof TIMER_UNITS)[number]

/* ---------------------------------------------------------------- date helpers (UTC day numbers) */

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/

function dayNumberOf(date: string): number {
  const m = DATE_RE.exec(date)
  if (!m) throw new RangeError(`Not a YYYY-MM-DD date: ${date}`)
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  const n = ms / DAY_MS
  if (dateOfDayNumber(n) !== date) throw new RangeError(`Not a calendar date: ${date}`)
  return n
}

function dateOfDayNumber(n: number): string {
  return new Date(n * DAY_MS).toISOString().slice(0, 10)
}

/** 1970-01-01 was a Thursday (4). */
function weekdayOfDayNumber(n: number): number {
  return (((n + 4) % 7) + 7) % 7
}

function minutesOf(time: string): number {
  return Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5))
}

/** Muscat wall clock of an instant: its day number and minutes since midnight (fractional below a minute). */
function muscatClock(instant: Date): { day: number; minutes: number } {
  const local = instant.getTime() + MUSCAT_OFFSET_MS
  const day = Math.floor(local / DAY_MS)
  return { day, minutes: (local - day * DAY_MS) / MINUTE_MS }
}

function instantOf(day: number, minutes: number): Date {
  return new Date(day * DAY_MS + minutes * MINUTE_MS - MUSCAT_OFFSET_MS)
}

function toDayNumber(value: Date | string): number {
  return typeof value === 'string' ? dayNumberOf(value) : muscatClock(value).day
}

function assertValidInstant(instant: Date): void {
  if (Number.isNaN(instant.getTime())) throw new RangeError('Invalid date')
}

function weekDayOf(cal: WorkingCalendar, day: number): WorkingDay {
  const d = cal.week[weekdayOfDayNumber(day)]
  if (!d) throw new RangeError('The week pattern must have 7 days')
  return d
}

function isWorkingDayNumber(cal: WorkingCalendar, day: number): boolean {
  return weekDayOf(cal, day).working && !cal.holidays.has(dateOfDayNumber(day))
}

function nextWorkingDayNumber(cal: WorkingCalendar, fromDay: number): number {
  for (let i = 1; i <= MAX_SCAN_DAYS; i++) {
    if (isWorkingDayNumber(cal, fromDay + i)) return fromDay + i
  }
  throw new RangeError(`No working day within ${MAX_SCAN_DAYS} days`)
}

/* ---------------------------------------------------------------- public API */

/** The business date (`YYYY-MM-DD`, Asia/Muscat) of an instant. */
export function muscatDate(instant: Date = new Date()): string {
  assertValidInstant(instant)
  return dateOfDayNumber(muscatClock(instant).day)
}

/** The weekday (0 = Sunday … 6 = Saturday) of an instant in Muscat, or of a `YYYY-MM-DD` date. */
export function muscatWeekday(value: Date | string): number {
  if (value instanceof Date) assertValidInstant(value)
  return weekdayOfDayNumber(toDayNumber(value))
}

/** True when the date (`YYYY-MM-DD`, or an instant's Muscat date) is a working day and not a holiday. */
export function isWorkingDay(cal: WorkingCalendar, date: Date | string): boolean {
  if (date instanceof Date) assertValidInstant(date)
  return isWorkingDayNumber(cal, toDayNumber(date))
}

/**
 * `days` working days after `start` (a non-negative integer). The start day is excluded and the wall-clock
 * time is kept; a start on a non-working day first moves to the next working day's start time.
 */
export function addWorkingDays(cal: WorkingCalendar, start: Date, days: number): Date {
  assertValidInstant(start)
  if (!Number.isInteger(days) || days < 0) throw new RangeError('days must be a non-negative integer')
  let { day, minutes } = muscatClock(start)
  if (!isWorkingDayNumber(cal, day)) {
    day = nextWorkingDayNumber(cal, day)
    minutes = minutesOf(weekDayOf(cal, day).start)
  }
  const first = day
  for (let i = 0; i < days; i++) {
    day = nextWorkingDayNumber(cal, day)
    if (day - first > MAX_SCAN_DAYS) throw new RangeError(`No working day within ${MAX_SCAN_DAYS} days`)
  }
  return instantOf(day, minutes)
}

/**
 * The working days in (from, to]: `from` excluded, `to` included. Negative when `to` is before `from`, so
 * `workingDaysBetween(cal, d, addWorkingDays(cal, d, n)) === n` for a start on a working day.
 */
export function workingDaysBetween(cal: WorkingCalendar, from: Date | string, to: Date | string): number {
  if (from instanceof Date) assertValidInstant(from)
  if (to instanceof Date) assertValidInstant(to)
  const a = toDayNumber(from)
  const b = toDayNumber(to)
  if (Math.abs(b - a) > MAX_SCAN_DAYS) throw new RangeError(`Range longer than ${MAX_SCAN_DAYS} days`)
  const [lo, hi, sign] = a <= b ? [a, b, 1] : [b, a, -1]
  let count = 0
  for (let d = lo + 1; d <= hi; d++) if (isWorkingDayNumber(cal, d)) count++
  return sign * count
}

/**
 * `hours` (non-negative, fractions allowed) of working time after `start`, consuming only the working-hours
 * windows. A start outside a window begins at the next window's start; a result exactly at a window's end
 * stays there.
 */
export function addWorkingHours(cal: WorkingCalendar, start: Date, hours: number): Date {
  assertValidInstant(start)
  if (!Number.isFinite(hours) || hours < 0) throw new RangeError('hours must be a non-negative number')
  let remaining = hours * 60
  let { day, minutes } = muscatClock(start)
  const first = day
  for (;;) {
    if (isWorkingDayNumber(cal, day)) {
      const wd = weekDayOf(cal, day)
      const open = minutesOf(wd.start)
      const close = minutesOf(wd.end)
      const from = Math.max(minutes, open)
      if (from <= close) {
        if (from + remaining <= close) return instantOf(day, from + remaining)
        remaining -= close - from
      }
    }
    day = nextWorkingDayNumber(cal, day)
    if (day - first > MAX_SCAN_DAYS) throw new RangeError(`No working time within ${MAX_SCAN_DAYS} days`)
    minutes = 0
  }
}

/**
 * When a timer of `amount` `unit` started at `start` fires. `HOURS` is exact elapsed time; the working units
 * follow the calendar (`addWorkingHours`, `addWorkingDays`).
 */
export function timerInstant(
  cal: WorkingCalendar,
  start: Date,
  timer: { amount: number; unit: TimerUnit },
): Date {
  assertValidInstant(start)
  switch (timer.unit) {
    case 'HOURS':
      if (!Number.isFinite(timer.amount) || timer.amount < 0) {
        throw new RangeError('amount must be a non-negative number')
      }
      return new Date(start.getTime() + timer.amount * 60 * MINUTE_MS)
    case 'WORKING_HOURS':
      return addWorkingHours(cal, start, timer.amount)
    case 'WORKING_DAYS':
      return addWorkingDays(cal, start, timer.amount)
  }
}

/* ---------------------------------------------------------------- Hijri display (ASM-135) */

const hijriFormatters = new Map<string, Intl.DateTimeFormat>()

function hijriFormatter(
  key: string,
  locale: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  let f = hijriFormatters.get(key)
  if (!f) {
    f = new Intl.DateTimeFormat(`${locale}-u-ca-islamic-umalqura-nu-latn`, {
      ...options,
      timeZone: 'Asia/Muscat',
    })
    hijriFormatters.set(key, f)
  }
  return f
}

/** The Umm al-Qura Hijri date of an instant's Muscat day, e.g. "Ramadan 1, 1447 AH" / "1 رمضان 1447 هـ". */
export function hijriDate(instant: Date, language: 'en' | 'ar'): string {
  assertValidInstant(instant)
  return hijriFormatter(language, language, { day: 'numeric', month: 'long', year: 'numeric' }).format(
    instant,
  )
}

/** The numeric Umm al-Qura Hijri date of an instant's Muscat day. */
export function hijriDateParts(instant: Date): { year: number; month: number; day: number } {
  assertValidInstant(instant)
  const parts = hijriFormatter('parts', 'en', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  }).formatToParts(instant)
  const get = (type: 'year' | 'month' | 'day') => Number(parts.find((p) => p.type === type)?.value)
  return { year: get('year'), month: get('month'), day: get('day') }
}
